import { ClassName } from '@boxcryptor-web/shared/class-name';
import { createUseContext } from '@boxcryptor-web/shared/react-hooks';
import classnames from 'classnames';
import * as React from 'react';
import { overrideTailwindClasses } from 'tailwind-override';
import { Link, LinkProps } from './link/Link';

type ButtonSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';
type ButtonVariant = 'primary' | 'secondary' | 'white' | 'white-brand';

function getButtonClasses(size: ButtonSize = 'base', variant: ButtonVariant = 'white', disabled?: boolean) {
  const sizing: Record<ButtonSize, string> = {
    xs: 'px-2.5 py-1.5 text-xs leading-4 rounded',
    sm: 'px-3 py-2 text-sm leading-4 rounded-md',
    base: 'px-4 py-2 text-sm leading-5 rounded-md',
    lg: 'px-4 py-2 text-base leading-6 rounded-md',
    xl: 'px-6 py-3 text-base leading-6 rounded-md',
  };

  const colors: Record<ButtonVariant, string> = {
    primary: 'border-transparent text-white bg-brand-500 active:bg-brand-600',
    secondary: 'border-transparent text-brand-700 bg-brand-100 active:bg-brand-200',
    white: 'border-gray-300 text-gray-700 bg-white active:text-gray-800 active:bg-gray-50',
    'white-brand': 'text-brand-600 bg-white active:text-gray-800 active:bg-gray-50',
  };

  const hover: Record<ButtonVariant, string> = {
    primary: 'hover:bg-brand-400',
    secondary: 'hover:bg-brand-50',
    white: 'hover:text-gray-500',
    'white-brand': 'hover:bg-brand-50',
  };

  const focus: Record<ButtonVariant, string> = {
    primary: 'focus:ring-1 dark:focus:ring-2 focus:ring-inset focus:ring-white ',
    secondary: 'focus:ring-1 focus:ring-brand-700',
    white: 'focus:outline-none focus:border-focus',
    'white-brand': 'focus:outline-none focus:border-focus',
  };

  return classnames(
    'inline-flex items-center border font-medium focus:outline-none transition ease-in-out duration-150',
    sizing[size],
    colors[variant],
    !disabled && hover[variant],
    !disabled && focus[variant],
    disabled && 'opacity-50'
  );
}

const ButtonContext = React.createContext({ fullOnMobile: false });

const useButtonContext = createUseContext('ButtonContext', ButtonContext);

export const Button: React.FC<
  ClassName<{
    children?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    wrapperClassName?: string;
    size?: ButtonSize;
    variant?: ButtonVariant;
    type?: 'submit' | 'reset' | 'button';
    disabled?: boolean;
  }>
> = (props) => {
  const { onClick, className, size, disabled, variant, type = 'button', children, wrapperClassName } = props;

  const { fullOnMobile } = useButtonContext();

  return (
    <span className={classnames('inline-flex rounded-md shadow-sm', fullOnMobile ? 'w-full sm:w-auto' : 'w-auto', wrapperClassName)}>
      <button
        type={type}
        disabled={disabled}
        onClick={onClick}
        className={overrideTailwindClasses(classnames('w-full justify-center', getButtonClasses(size, variant, disabled), className))}
      >
        {children}
      </button>
    </span>
  );
};

export const LinkButton: React.FC<
  ClassName<{
    wrapperClassName?: string;
    size?: ButtonSize;
    variant?: ButtonVariant;
  }> &
    LinkProps
> = (props) => {
  const { className, wrapperClassName, size, variant, children, ...other } = props;
  const { fullOnMobile } = useButtonContext();
  return (
    <span
      className={overrideTailwindClasses(
        classnames('inline-flex rounded-md shadow-sm', fullOnMobile ? 'w-full sm:w-auto' : 'w-auto', wrapperClassName)
      )}
    >
      <Link
        {...other}
        className={overrideTailwindClasses(
          classnames('w-full', getButtonClasses(size, variant), className, 'flex justify-center no-underline')
        )}
      >
        {children}
      </Link>
    </span>
  );
};

export const PseudoButton: React.FC<
  ClassName<{
    children?: React.ReactNode;
    size?: ButtonSize;
    variant?: ButtonVariant;
    wrapperClassName?: string;
  }>
> = (props) => {
  const { className, wrapperClassName, size, variant, children } = props;
  const { fullOnMobile } = useButtonContext();
  return (
    <span
      className={overrideTailwindClasses(
        classnames('inline-flex rounded-md shadow-sm', fullOnMobile ? 'w-full sm:w-auto' : 'w-auto', wrapperClassName)
      )}
    >
      <div
        className={overrideTailwindClasses(classnames('w-full justify-center cursor-pointer', getButtonClasses(size, variant), className))}
      >
        {children}
      </div>
    </span>
  );
};

export const ButtonsFullOnMobile: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <ButtonContext.Provider value={{ fullOnMobile: true }}>{children}</ButtonContext.Provider>
);
